/**
 * O formato alvo. A diferenca essencial para a v1 e que aqui **nada e
 * opcional**.
 *
 * Na v1, `elements` e uma lista que pode ter 0, 7 ou 14 itens, cada um com
 * campos ausentes. Por isso o render atual esta cheio de `getStyle(id)?.color
 * ?? fallback` — 38 ocorrencias so no preview. Cada um desses fallbacks e uma
 * chance de dois renderizadores escolherem defaults diferentes, e foi
 * exatamente o que aconteceu.
 *
 * Na v2, `elements` e um Record TOTAL: os 14 ids sempre presentes, todos os
 * campos preenchidos. Depois da normalizacao nao existe `undefined` para
 * ninguem interpretar, e o `resolve` nunca escolhe default — ele so le.
 *
 * Unidades tambem sao resolvidas aqui:
 *   - posicao vira fracao 0..1 (a v1 usa 0..100)
 *   - largura vira fracao 0..1 da pagina (a v1 usa pixels do canvas 1123)
 *   - letterSpacing vira numero em `em` (a v1 usa string "0.2em")
 *
 * Fracao em vez de pixel/percentual porque o Primitive final e em pontos, e a
 * conversao precisa acontecer em UM lugar so (`layout.ts:toPt()`). Guardar
 * pixel aqui obrigaria cada consumidor a saber que o canvas era 1123.
 */

import type { ElementId } from '../types.js';

export type FontSizeToken =
  | 'xs' | 'sm' | 'base' | 'lg' | 'xl'
  | '2xl' | '3xl' | '4xl' | '5xl';

export type FontWeight = 'normal' | 'semibold' | 'bold';
export type TextAlign = 'left' | 'center' | 'right';
export type EventDateMode = 'start' | 'end' | 'range';

/** Corpo em pixels do canvas web, por token. Convertido a pt no layout. */
export const FONT_SIZE_PX: Readonly<Record<FontSizeToken, number>> = {
  xs: 12, sm: 14, base: 16, lg: 18, xl: 20,
  '2xl': 24, '3xl': 30, '4xl': 36, '5xl': 48,
};

export interface V2Style {
  /**
   * Cor propria em hex `#rrggbb`, ou null para herdar a cor global.
   *
   * REGRA R1: quando ha cor propria, `opacity` e IGNORADA. A regra nasceu no
   * editor web (aparece la 8 vezes, copiada) e nunca chegou ao renderer de
   * PDF — e a origem da divergencia mais visivel entre preview e e-mail.
   * Aqui ela mora em UM lugar so: `theme.ts:resolveInk()`.
   */
  readonly color: string | null;
  /** 0..1. Ver R1 acima: so vale quando `color` e null. */
  readonly opacity: number;
  readonly fontSize: FontSizeToken;
  readonly fontWeight: FontWeight;
  readonly textAlign: TextAlign;
  readonly uppercase: boolean;
  /** Em unidades `em`. 0 = normal. */
  readonly letterSpacing: number;
}

export interface V2Element {
  readonly id: ElementId;
  readonly enabled: boolean;
  /** Fracao 0..1 da largura da pagina. */
  readonly x: number;
  /** Fracao 0..1 da altura da pagina. */
  readonly y: number;
  /** Fracao 0..1 da largura, ou null para ajustar ao conteudo. */
  readonly width: number | null;
  readonly style: V2Style;
}

export interface V2Fields {
  readonly showParticipantName: boolean;
  readonly showParticipantLastName: boolean;
  /**
   * Se o documento aparece. NAO controla se aparece inteiro — o documento e
   * sempre mascarado. O certificado e alcancavel por URL publica com o codigo,
   * e o produtor nao tem autoridade para publicar CPF completo de terceiro
   * numa pagina verificavel (LGPD). Decisao de 2026-08-27.
   */
  readonly showDocument: boolean;
  readonly showEventName: boolean;
  readonly showEventDate: boolean;
  readonly showEventTime: boolean;
  readonly showEventLocation: boolean;
  readonly showWorkload: boolean;
  readonly showSigners: boolean;
  readonly showQRCode: boolean;
  readonly eventDateMode: EventDateMode;
}

export interface V2Text {
  readonly title: string;
  readonly introText: string;
  readonly participationText: string;
  readonly conclusionText: string;
}

export interface V2Signer {
  readonly name: string;
  readonly role: string;
  readonly signatureUrl: string | null;
}

export interface V2Config {
  readonly version: 2;
  /** Cor global em hex `#rrggbb`. Sempre valida apos a normalizacao. */
  readonly textColor: string;
  readonly backgroundUrl: string | null;
  /** Total: os 14 ids sempre presentes. */
  readonly elements: Readonly<Record<ElementId, V2Element>>;
  readonly fields: V2Fields;
  readonly text: V2Text;
  readonly signers: readonly V2Signer[];
  readonly workloadHours: number | null;
  /** Elegibilidade, nao layout. Preservado por nao ser nosso. */
  readonly eligibleTierIds: readonly string[];
}

export const V2_DEFAULT_TEXT_COLOR = '#1f2937';

export const V2_DEFAULT_FIELDS: V2Fields = {
  showParticipantName: true,
  showParticipantLastName: true,
  showDocument: false,
  showEventName: true,
  showEventDate: true,
  showEventTime: false,
  showEventLocation: true,
  showWorkload: false,
  showSigners: true,
  showQRCode: true,
  eventDateMode: 'start',
};

export const V2_DEFAULT_STYLE: V2Style = {
  color: null,
  opacity: 1,
  fontSize: 'base',
  fontWeight: 'normal',
  textAlign: 'center',
  uppercase: false,
  letterSpacing: 0,
};
